import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/dark.css";
import { Trash2, Save, Download, FileText, Upload, Copy } from 'lucide-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import WorkingCapitalMaster from './WorkingCapitalMaster';
import CategoryMaster from './CategoryMaster';
import TemplatesList from './TemplatesList';
import BankFinancialProjection from './BankFinancialProjection';
import ProjectPercentageMaster from './ProjectPercentageMaster';
import CostOfProject from './CostOfProject';
import { useLocation } from 'react-router-dom';
// ─── Format number as Indian currency ────────────────────────────────────────
const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ─── React Select Custom Styles ──────────────────────────────────────────────
const selectStyles = {
  indicatorSeparator: () => ({ display: 'none' }),
  control: (base, state) => ({
    ...base,
    backgroundColor: '#020617', // slate-950
    borderColor: state.isFocused ? '#ffffff80' : '#475569', // slate-600
    color: 'white',
    boxShadow: state.isFocused ? '0 0 0 1px #ffffff33' : 'none',
    minHeight: '34px',
    height: '34px',
    borderRadius: '0.5rem',
    cursor: 'text',
    '&:hover': { borderColor: '#ffffff80' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, color: 'white', margin: 0, padding: 0, caretColor: 'transparent' }),
  singleValue: (base) => ({ ...base, color: 'white', fontSize: '0.875rem' }),
  placeholder: (base) => ({ ...base, color: '#475569', fontSize: '0.875rem' }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#0f172a', // slate-900
    border: '1px solid #475569',
    zIndex: 9999,
    borderRadius: '0.5rem',
    overflow: 'hidden'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#1e293b' : 'transparent', // slate-800
    color: '#e2e8f0',
    fontSize: '0.875rem',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#334155' },
  }),
};

// ═══════════════════════════════════════════════════════════════════════════════
//  SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const GreenBanner = ({ label, amount, amountLabel = 'Per Annum' }) => (
  <div className="flex items-center justify-between bg-green-700/30 border-l-4 border-green-500 border border-green-500/30 rounded-xl px-5 py-3">
    <span className="text-green-300 font-black text-base uppercase tracking-widest">{label}</span>
    <div className="text-right">
      <span className="text-xs text-green-500/70 mr-2 font-mono">{amountLabel}</span>
      <span className="text-green-300 font-black text-lg font-mono">₹ {fmt(amount)}</span>
    </div>
  </div>
);

const SubHeader = ({ title, total }) => (
  <div className="flex items-center justify-between bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-4 py-2 mb-2">
    <span className="text-indigo-300 font-bold text-sm">{title}</span>
    <div className="text-right">
      <span className="text-xs text-indigo-400/60 mr-2">Per Annum</span>
      <span className="text-indigo-200 font-black font-mono">₹ {fmt(total)}</span>
    </div>
  </div>
);

const TableHeader = ({ cols }) => (
  <thead>
    <tr className="bg-indigo-500/20 border-b-2 border-indigo-500/40">
      {cols.map((c, i) => (
        <th key={i} className={`py-2.5 px-3 text-indigo-300 text-xs font-bold uppercase tracking-wider whitespace-nowrap ${i > 0 ? 'text-right' : 'text-left'}`}>
          {c}
        </th>
      ))}
      <th className="py-2.5 px-2 text-center text-indigo-500/50 text-xs font-bold uppercase">Del</th>
    </tr>
  </thead>
);

const TotalRow = ({ label, cols = 6, values = [] }) => (
  <tr className="bg-indigo-500/10 border-t-2 border-indigo-500/30">
    <td colSpan={cols - values.length + 1} className="px-3 py-2.5 font-bold text-indigo-300 text-xs uppercase tracking-widest">{label}</td>
    {values.map((v, i) => (
      <td key={i} className="px-3 py-2.5 text-right font-bold text-indigo-300 font-mono text-sm">{v !== null ? `₹ ${fmt(v)}` : ''}</td>
    ))}
    <td />
  </tr>
);

const CalcCell = ({ value, highlight = false }) => (
  <td className={`px-3 py-2 text-right font-mono text-sm whitespace-nowrap ${highlight ? 'text-green-400 font-bold bg-green-950/30' : 'text-green-400/70 bg-transparent'}`}>
    {fmt(value)}
  </td>
);

const InputCell = ({ value, onChange }) => (
  <td className="px-2 py-1" style={{ minWidth: '110px' }}>
    <input
      type="number" min="0" step="any"
      value={value === 0 ? '' : value}
      onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
      placeholder="0"
      className="w-full bg-slate-950 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm text-right font-mono outline-none focus:border-white/50 focus:ring-1 focus:ring-white/20 transition placeholder-slate-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
  </td>
);

const TextCell = ({ value, onChange, options = [] }) => {
  const selectedOption = value ? { label: value, value: value } : null;
  return (
    <td className="px-2 py-1" style={{ minWidth: '250px' }}>
      <CreatableSelect
        isSearchable={true}
        styles={selectStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        options={options}
        value={selectedOption}
        onChange={(newValue) => onChange(newValue ? newValue.value : '')}
        placeholder="Select or Type Item"
        formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
      />
    </td>
  );
};

const RemoveBtn = ({ onClick, disabled }) => (
  <td className="px-2 py-1 text-center">
    <button type="button" onClick={onClick} disabled={disabled}
      className="text-rose-500 hover:text-rose-400 disabled:opacity-20 transition p-1 rounded hover:bg-rose-500/10">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  </td>
);

const AddRowBtn = ({ onClick, label = 'Add Row' }) => (
  <button type="button" onClick={onClick}
    className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-4 py-1.5 rounded-lg border border-slate-700 transition">
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
    {label}
  </button>
);

// ─── Default new row ──────────────────────────────────────────────────────────
const newRow = (description = '') => ({ description, units: 0, rate: 0, per_day: 0, per_month: 0, per_annum: 0 });

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const HospitalReport = () => {
  const location = useLocation();
  const [reportsList, setReportsList] = useState([]);
  const [reportId, setReportId] = useState(null);
  const [reportName, setReportName] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [workingDays, setWorkingDays] = useState(26);
  const [workingHours, setWorkingHours] = useState(8);
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [cloneModal, setCloneModal] = useState({ isOpen: false, name: '', cloneType: 'SAME_CLIENT', newClientId: '' });
  const [clientCloneModal, setClientCloneModal] = useState({ isOpen: false, sourceReportId: '', newName: '' });
  const [filterClientId, setFilterClientId] = useState('');
  const [clientsList, setClientsList] = useState([]);
  const [clientId, setClientId] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [templateSections, setTemplateSections] = useState(null);
  const [dbCategories, setDbCategories] = useState([]);
  const [openSections, setOpenSections] = useState({ settings: false });
  const [fyPercentages, setFyPercentages] = useState([]);
  const [activeTab, setActiveTab] = useState('projections');
  const [projectionsSubTab, setProjectionsSubTab] = useState('report'); // 'report' | 'cost_of_project'
  const [selectedMainId, setSelectedMainId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const urlLoadedRef = useRef(false);
  const [projectionYears, setProjectionYears] = useState(15);

  // ── Excel Import State ────────────────────────────────────────────────────────
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { sheetName, sections: [{label, rows: [{description, units, rate, per_day, per_month, per_annum}]}] }
  const [importLoading, setImportLoading] = useState(false);
  const [importMode, setImportMode] = useState('replace'); // 'replace' | 'append'
  const excelInputRef = useRef(null);

  // ── dynamicRows: { [sub_category_id]: Row[] }
  const [dynamicRows, setDynamicRows] = useState({});

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const toggle = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  const getCatName = useCallback((id) => {
    if (!id) return '';
    const cat = dbCategories.find(c => c.id === Number(id));
    return cat ? cat.name : '';
  }, [dbCategories]);

  // ── Calculate a row's derived values ─────────────────────────────────────────
  const calcRow = useCallback((row) => {
    const per_day = (row.units || 0) * (row.rate || 0);
    const per_month = per_day * workingDays;
    const per_annum = per_month * 12;
    return { ...row, per_day, per_month, per_annum };
  }, [workingDays]);

  // ── Build a structured tree from template sections ───────────────────────────
  // Returns: [{ mainId, mainName, isExpenditure, categories: [{ catId, catName, subCategories: [{ subId, subName, items: [...] }] }] }]
  const buildTree = useCallback(() => {
    if (!templateSections || templateSections.length === 0) return [];

    const mainMap = new Map();
    templateSections.forEach(sec => {
      const mainId = sec.main_section_id;
      const catId = sec.category_id;
      const subId = sec.sub_category_id;
      if (!mainId) return;

      if (!mainMap.has(mainId)) {
        const mainName = getCatName(mainId) || `Section ${mainId}`;
        const isExpenditure = mainName.toUpperCase().includes('EXPENDITURE') && !mainName.toUpperCase().includes('INCOME AND EXPENDITURE');
        mainMap.set(mainId, { mainId, mainName, isExpenditure, catMap: new Map() });
      }
      const main = mainMap.get(mainId);

      if (catId) {
        if (!main.catMap.has(catId)) {
          const catName = getCatName(catId) || `Category ${catId}`;
          const isCatExpenditure = catName.toUpperCase().includes('EXPENDITURE') || catName.toUpperCase().includes('EXPENSE');
          main.catMap.set(catId, { catId, catName, isCatExpenditure, subMap: new Map() });
        }
        const cat = main.catMap.get(catId);

        if (subId) {
          if (!cat.subMap.has(subId)) {
            const rawItems = sec.items ? (typeof sec.items === 'string' ? JSON.parse(sec.items) : sec.items) : [];
            cat.subMap.set(subId, { subId, subName: getCatName(subId) || `Sub-category ${subId}`, items: rawItems.map(Number) });
          }
        }
      }
    });

    return Array.from(mainMap.values()).map(m => ({
      ...m,
      categories: Array.from(m.catMap.values()).map(c => ({
        ...c,
        subCategories: Array.from(c.subMap.values())
      }))
    }));
  }, [templateSections, getCatName]);

  const tree = buildTree();

  // ── Initialize dynamicRows whenever template changes ─────────────────────────
  useEffect(() => {
    if (!templateSections || templateSections.length === 0 || dbCategories.length === 0) {
      return;
    }
    setDynamicRows(prev => {
      const next = { ...prev };
      let changed = false;
      templateSections.forEach(sec => {
        const subId = sec.sub_category_id;
        if (!subId) return;
        const key = String(subId);

        // If not seeded yet, or seeded with empty strings due to previous race condition
        const needsSeeding = !next[key] || (
          next[key].every(r => r.description === '') &&
          sec.items && sec.items.length > 0
        );

        if (needsSeeding) {
          // Seed with item names as default rows
          const rawItems = sec.items ? (typeof sec.items === 'string' ? JSON.parse(sec.items) : sec.items) : [];
          const itemRows = rawItems.map(itemId => {
            const cat = dbCategories.find(c => c.id === Number(itemId));
            return newRow(cat ? cat.name : '');
          });

          if (itemRows.length > 0 && itemRows.some(r => r.description !== '')) {
            next[key] = itemRows;
            changed = true;
          } else if (!next[key]) {
            next[key] = [newRow()];
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [templateSections, dbCategories]);

  // ── Computed rows with calculations ─────────────────────────────────────────
  const computedRows = useCallback((subId) => {
    const key = String(subId);
    const rows = dynamicRows[key] || [newRow()];
    return rows.map(calcRow);
  }, [dynamicRows, calcRow]);

  // ── Profit Percentage Helper ──────────────────────────────────────────────────
  const getProfitPerc = useCallback((subId) => {
    if (!subId) return 100;
    const cat = dbCategories.find(c => c.id === Number(subId));
    if (cat) {
      if (cat.profit_perc !== null && cat.profit_perc !== undefined) return parseFloat(cat.profit_perc);
      if (cat.pharmacy_perc !== null && cat.pharmacy_perc !== undefined) return parseFloat(cat.pharmacy_perc);
      if (cat.xray_perc !== null && cat.xray_perc !== undefined) return parseFloat(cat.xray_perc);
    }
    return 100;
  }, [dbCategories]);

  // ── Section total ────────────────────────────────────────────────────────────
  const sectionTotal = useCallback((subId, subName = '') => {
    const gross = computedRows(subId).reduce((s, r) => s + r.per_annum, 0);
    return (gross * getProfitPerc(subId)) / 100;
  }, [computedRows, getProfitPerc]);

  // ── Grand totals ─────────────────────────────────────────────────────────────
  const { totalExpAnnum, totalIncAnnum } = React.useMemo(() => {
    let exp = 0, inc = 0;
    tree.forEach(main => {
      main.categories.forEach(cat => {
        cat.subCategories.forEach(sub => {
          const tot = sectionTotal(sub.subId, sub.subName);
          // If the main section is Income & Expenditure, we must look at the Category level
          if (main.mainName.toUpperCase() === 'INCOME AND EXPENDITURE') {
            if (cat.isCatExpenditure) exp += tot;
            else inc += tot;
          } else {
            if (main.isExpenditure) exp += tot;
            else inc += tot;
          }
        });
      });
    });
    return { totalExpAnnum: exp, totalIncAnnum: inc };
  }, [tree, sectionTotal]);

  const netProfitLoss = totalIncAnnum - totalExpAnnum;

  // ── Row CRUD ─────────────────────────────────────────────────────────────────
  const updateDynRow = (subId, rowIdx, field, val) => {
    const key = String(subId);
    setDynamicRows(prev => {
      const rows = [...(prev[key] || [])];
      rows[rowIdx] = { ...rows[rowIdx], [field]: val };
      return { ...prev, [key]: rows };
    });
  };

  const addDynRow = (subId) => {
    const key = String(subId);
    setDynamicRows(prev => ({ ...prev, [key]: [...(prev[key] || []), newRow()] }));
  };

  const removeDynRow = (subId, rowIdx) => {
    const key = String(subId);

    setConfirmModal({
      isOpen: true, title: 'Delete Row', message: 'Delete this row?',
      onConfirm: async () => {
        const currentRows = dynamicRows[key] || [];
        let newSectionRows;
        if (currentRows.length <= 1) {
          // If it's the last row, just reset it to a blank row instead of removing it entirely
          newSectionRows = [newRow()];
        } else {
          newSectionRows = currentRows.filter((_, i) => i !== rowIdx);
        }
        const nextRows = { ...dynamicRows, [key]: newSectionRows };

        // Optimistic UI update
        setDynamicRows(nextRows);
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });

        // Auto-save to sync DB
        await saveReport(nextRows);
        toast.success('Row deleted successfully');
      }
    });
  };

  // ── Fetch initial data ────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true })
      .then(res => setReportsList(res.data))
      .catch(() => { });
    axios.get(`${process.env.REACT_APP_API_URL}/api/categories`, { withCredentials: true })
      .then(res => setDbCategories(res.data))
      .catch(() => { });
    axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, { withCredentials: true })
      .then(res => setClientsList(res.data))
      .catch(() => { });
  }, []);

  // ── Auto-load project from URL param ?project_id=X ───────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlProjectId = params.get('project_id');
    if (urlProjectId && !urlLoadedRef.current) {
      urlLoadedRef.current = true;
      loadReport(urlProjectId);
    } else if (!urlProjectId) {
      urlLoadedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── Watch clientId → fetch template sections ──────────────────────────────────
  useEffect(() => {
    if (!clientId) { setTemplateSections(null); setTemplateId(null); return; }
    axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, { withCredentials: true })
      .then(res => {
        setClientsList(res.data);
        const client = res.data.find(c => c.id === Number(clientId));
        if (client) {
          let tId = client.template_id;
          if (!tId && client.template_ids) {
            let ids = typeof client.template_ids === 'string' ? JSON.parse(client.template_ids) : client.template_ids;
            if (ids && ids.length > 0) {
              return axios.get(`${process.env.REACT_APP_API_URL}/api/templates?include_custom=true`, { withCredentials: true })
                .then(tmplRes => {
                  const allTemplates = tmplRes.data;
                  const customIds = ids.filter(id => allTemplates.some(t => t.id === Number(id) && t.is_custom));
                  const targetIds = customIds.length > 0 ? customIds : ids;
                  setTemplateId(targetIds.join(','));
                  return Promise.all(targetIds.map(id => axios.get(`${process.env.REACT_APP_API_URL}/api/templates/${id}/sections`, { withCredentials: true })));
                });
            }
          }
          if (tId) {
            setTemplateId(tId);
            return Promise.all([axios.get(`${process.env.REACT_APP_API_URL}/api/templates/${tId}/sections`, { withCredentials: true })]);
          }
        }
        setTemplateSections(null);
        setTemplateId(null);
        return null;
      })
      .then(res => {
        if (res && Array.isArray(res)) {
          let mergedSections = [];
          res.forEach(response => {
            if (response.data && response.data.length > 0) {
              mergedSections = mergedSections.concat(response.data);
            }
          });
          setTemplateSections(mergedSections);
        }
      })
      .catch((e) => { console.error(e); setTemplateSections(null); setTemplateId(null); });
  }, [clientId]);

  // ── Reset state ───────────────────────────────────────────────────────────────
  const resetState = () => {
    setReportId(null); setReportName(''); setReportDate('');
    setClientId(''); setWorkingDays(26); setWorkingHours(8);
    setDynamicRows({}); setTemplateSections(null); setFyPercentages([]); setTemplateId(null);
  };

  // ── Load report ───────────────────────────────────────────────────────────────
  const loadReport = async (id) => {
    if (!id) { resetState(); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${id}`, { withCredentials: true });
      const d = res.data;
      setReportId(d.id);
      setReportName(d.report_name);
      setReportDate(d.report_date || '');
      setWorkingDays(d.working_days_per_month || 26);
      setWorkingHours(d.working_hours_per_day || 8);
      setClientId(d.client_id || '');
      setFilterClientId(d.client_id || '');

      // Load dynamic rows from financial_report_data grouped by sub_category_id
      if (d.financial_data) {
        const loaded = {};
        Object.entries(d.financial_data).forEach(([refId, rows]) => {
          // refId here is sub_category_id stored in ref_id field
          loaded[refId] = rows.map(r => ({
            description: r.item_name || '',
            units: parseFloat(r.units) || 0,
            rate: parseFloat(r.rate) || 0,
            per_day: 0, per_month: 0, per_annum: 0
          }));
        });
        setDynamicRows(loaded);
      }

      // Load FY percentages
      try {
        const fyRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/project-percentage`, {
          params: { report_id: id }, withCredentials: true
        });
        setFyPercentages(fyRes.data || []);
      } catch { setFyPercentages([]); }

      toast.success('Report loaded!');
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  // ── Create report ─────────────────────────────────────────────────────────────
  const createReport = async () => {
    if (!reportName.trim()) { toast.error('Report name required'); return; }
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, {
        report_name: reportName, report_date: reportDate,
        working_days_per_month: workingDays, working_hours_per_day: workingHours,
        client_id: clientId || null
      }, { withCredentials: true });
      const newId = res.data.id;
      setReportId(newId);
      setReportsList(prev => [{ id: newId, report_name: reportName }, ...prev]);
      setShowNewReportModal(false);
      toast.success('Report created!');
    } catch { toast.error('Failed to create report'); }
  };

  // ── Export to Excel ───────────────────────────────────────────────────────
  const exportToExcel = () => {
    if (!reportId) return toast.error('Select a report first');

    const cell = (v, s) => ({ v, s, t: typeof v === 'number' ? 'n' : 's' });
    const styles = {
      title: { font: { bold: true, sz: 16 } },
      secHeader: { font: { bold: true, sz: 12, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF059669' } } },
      catHeader: { font: { bold: true, sz: 11, color: { rgb: 'FF1E293B' } }, fill: { fgColor: { rgb: 'FFE2E8F0' } } },
      text: { font: { color: { rgb: 'FF1E293B' } } },
      number: { font: { color: { rgb: 'FF1E293B' } }, alignment: { horizontal: 'right' } },
      boldNumber: { font: { bold: true, color: { rgb: 'FF1E293B' } }, alignment: { horizontal: 'right' } },
    };

    const wsData = [
      [cell(`HOSPITAL REPORT: ${reportName}`, styles.title)],
      [],
    ];

    tree.forEach(main => {
      wsData.push([cell(main.mainName, styles.secHeader), cell('', styles.secHeader), cell('', styles.secHeader), cell('', styles.secHeader), cell('', styles.secHeader), cell('', styles.secHeader)]);

      main.categories.forEach(cat => {
        cat.subCategories.forEach(sub => {
          wsData.push([cell(sub.subName, styles.catHeader), cell('', styles.catHeader), cell('', styles.catHeader), cell('', styles.catHeader), cell('', styles.catHeader), cell('', styles.catHeader)]);
          wsData.push([cell('Description', styles.text), cell('Units', styles.text), cell('Rate', styles.text), cell('Per Day', styles.text), cell('Per Month', styles.text), cell('Per Annum', styles.text)]);

          const rows = computedRows(sub.subId);
          rows.forEach(r => {
            if (r.description) {
              wsData.push([
                cell(r.description, styles.text),
                cell(r.units || 0, styles.number),
                cell(r.rate || 0, styles.number),
                cell(r.per_day || 0, styles.number),
                cell(r.per_month || 0, styles.number),
                cell(r.per_annum || 0, styles.number)
              ]);
            }
          });

          const total = sectionTotal(sub.subId, sub.subName);
          wsData.push([cell('TOTAL', styles.text), cell('', styles.text), cell('', styles.text), cell('', styles.text), cell('', styles.text), cell(total, styles.boldNumber)]);
          wsData.push([]);
        });
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hospital Report');
    XLSX.writeFile(wb, `${reportName}_Hospital_Report.xlsx`);
    toast.success('Excel Exported!');
  };

  // ── Export to PDF ─────────────────────────────────────────────────────────
  const exportToPDF = () => {
    if (!reportId) return toast.error('Select a report first');
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.setFontSize(16);
    doc.text(`HOSPITAL REPORT: ${reportName}`, 40, 40);

    let currentY = 70;

    tree.forEach(main => {
      doc.setFontSize(12);
      doc.setTextColor(5, 150, 105);
      doc.text(main.mainName, 40, currentY);
      currentY += 15;

      main.categories.forEach(cat => {
        cat.subCategories.forEach(sub => {
          const rows = computedRows(sub.subId).filter(r => r.description);
          if (rows.length === 0) return;

          const total = sectionTotal(sub.subId, sub.subName);

          const tableData = rows.map(r => [
            r.description,
            fmt(r.units),
            fmt(r.rate),
            fmt(r.per_day),
            fmt(r.per_month),
            fmt(r.per_annum)
          ]);

          tableData.push([
            { content: 'TOTAL', styles: { fontStyle: 'bold' } },
            '', '', '', '',
            { content: fmt(total), styles: { fontStyle: 'bold', halign: 'right' } }
          ]);

          autoTable(doc, {
            startY: currentY,
            head: [[sub.subName, 'Units', 'Rate', 'Per Day', 'Per Month', 'Per Annum']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
            columnStyles: {
              0: { cellWidth: 160 },
              1: { halign: 'right' },
              2: { halign: 'right' },
              3: { halign: 'right' },
              4: { halign: 'right' },
              5: { halign: 'right' }
            },
            styles: { fontSize: 8 }
          });

          currentY = doc.lastAutoTable.finalY + 15;
          if (currentY > 750) {
            doc.addPage();
            currentY = 40;
          }
        });
      });
      currentY += 10;
    });

    doc.save(`${reportName}_Hospital_Report.pdf`);
    toast.success('PDF Exported!');
  };

  // ── Save report ───────────────────────────────────────────────────────────────
  const saveReport = async (customRows = null) => {
    if (!reportId) { toast.error('Select or create a report first'); return; }
    setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${reportId}`, {
        report_name: reportName, report_date: reportDate,
        working_days_per_month: workingDays, working_hours_per_day: workingHours,
        client_id: clientId || null
      }, { withCredentials: true });

      const base = `${process.env.REACT_APP_API_URL}/api/hospital-reports/${reportId}`;

      const isEvent = customRows && customRows.nativeEvent;
      const rowsToSave = (customRows && !isEvent) ? customRows : dynamicRows;

      // Save each sub_category's rows using the generic financial endpoint
      // refId = sub_category_id
      const saves = Object.entries(rowsToSave).map(([subId, rows]) => {
        const cRows = (rows || []).map(calcRow);
        // Find main/category info for this subId from tree
        let mainSec = '', catSec = '', subSec = '', mainSecId = null, catSecId = null;
        tree.forEach(m => {
          m.categories.forEach(c => {
            c.subCategories.forEach(s => {
              if (String(s.subId) === String(subId)) {
                mainSec = m.mainName; mainSecId = m.mainId;
                catSec = c.catName; catSecId = c.catId;
                subSec = s.subName;
              }
            });
          });
        });
        const payload = cRows.map(r => ({
          main_section: mainSec, category: catSec, sub_category: subSec,
          item_name: r.description, is_static: false,
          amount: r.per_annum,
          units: r.units, rate: r.rate,
          main_section_id: mainSecId, category_id: catSecId, sub_category_id: subId, item_id: null
        }));
        return axios.post(`${base}/financial/${subId}`, { rows: payload }, { withCredentials: true });
      });

      await Promise.all(saves);
      toast.success('Report saved successfully!');
    } catch (err) { toast.error('Save failed: ' + (err.response?.data || err.message)); }
    finally { setSaving(false); }
  };

  // ── Excel Import Logic ────────────────────────────────────────────────────────
  const SECTION_KEYWORDS = [
    { key: 'opd', labels: ['out patient', 'opd', 'o.p.d'] },
    { key: 'casualty', labels: ['casualty', 'emergency'] },
    { key: 'ipd', labels: ['in patient', 'ipd', 'i.p.d', 'inpatient'] },
    { key: 'ot', labels: ['operation theatre', 'o.t', 'theatre'] },
    { key: 'pharmacy', labels: ['pharmacy'] },
    { key: 'xray', labels: ['x-ray', 'xray', 'x ray', 'radiology'] },
    { key: 'laboratory', labels: ['laboratory', 'lab ', 'pathology'] },
    { key: 'sample_revenue', labels: ['sample revenue', 'sample'] },
  ];

  const detectSection = (text) => {
    const t = (text || '').toLowerCase();
    for (const s of SECTION_KEYWORDS) {
      if (s.labels.some(l => t.includes(l))) return s.key;
    }
    return null;
  };

  const findSubIdForSection = (sectionKey) => {
    const keyMap = {
      opd: ['opd', 'out patient', 'outpatient'],
      casualty: ['casualty', 'emergency'],
      ipd: ['ipd', 'in patient', 'inpatient'],
      ot: ['operation theatre', 'ot', 'theatre'],
      pharmacy: ['pharmacy'],
      xray: ['x-ray', 'xray', 'x ray', 'radiology'],
      laboratory: ['laboratory', 'lab', 'pathology'],
      sample_revenue: ['sample revenue', 'sample'],
    };
    const keywords = keyMap[sectionKey] || [];
    for (const main of tree) {
      for (const cat of main.categories) {
        for (const sub of cat.subCategories) {
          const name = (sub.subName || '').toLowerCase();
          if (keywords.some(k => name.includes(k))) return sub.subId;
        }
      }
    }
    return null;
  };

  const parseExcelFile = (file) => {
    setImportLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });

        // Pick best sheet: prefer one with revenue/annexure/income in name
        let bestSheet = wb.SheetNames[0];
        for (const name of wb.SheetNames) {
          const n = name.toLowerCase();
          if (n.includes('revenue') || n.includes('annexure') || n.includes('income') || n.includes('projection')) {
            bestSheet = name; break;
          }
        }

        const ws = wb.Sheets[bestSheet];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        const cleanNum = (v) => {
          if (typeof v === 'number') return v;
          const s = String(v || '').replace(/[,₹\s]/g, '');
          return parseFloat(s) || 0;
        };

        const sections = [];
        let currentSection = null;

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const rowText = row.map(c => String(c)).join(' ').toLowerCase();
          if (!rowText.trim()) continue;

          // Detect section header
          const sectionKey = detectSection(rowText);
          if (sectionKey && row.filter(c => c !== '').length <= 5) {
            currentSection = { key: sectionKey, label: String(row.filter(c => c !== '')[0] || sectionKey), rows: [] };
            sections.push(currentSection);
            continue;
          }

          // Skip column header rows
          if (rowText.includes('unit') && (rowText.includes('rate') || rowText.includes('per day'))) continue;

          // Parse data row — needs description + at least one positive number
          if (currentSection) {
            const desc = String(row[1] || row[2] || '').trim();
            if (!desc || desc.length < 2) continue;
            const allNums = [];
            for (let j = 0; j < row.length; j++) {
              const n = cleanNum(row[j]);
              if (n > 0) allNums.push(n);
            }
            if (allNums.length === 0) continue;

            let units = 0, rate = 0, per_day = 0, per_month = 0, per_annum = 0;
            if (allNums.length >= 5) { [units, rate, per_day, per_month, per_annum] = allNums; }
            else if (allNums.length === 4) { [units, rate, per_month, per_annum] = allNums; per_day = per_month / 26; }
            else if (allNums.length === 3) { [per_day, per_month, per_annum] = allNums; }
            else if (allNums.length === 2) { [per_month, per_annum] = allNums; }
            else { per_annum = allNums[0]; }

            currentSection.rows.push({ description: desc, units, rate, per_day, per_month, per_annum });
          }
        }

        // Fallback: if no sections detected, load all rows as generic
        const filtered = sections.filter(s => s.rows.length > 0);
        if (filtered.length === 0) {
          const fallbackRows = [];
          for (const row of rows) {
            const desc = String(row[1] || row[0] || '').trim();
            if (!desc || desc.length < 2) continue;
            const per_annum = cleanNum(row[5]) || cleanNum(row[4]) || cleanNum(row[3]) || 0;
            const units = cleanNum(row[2]) || 0;
            const rate = cleanNum(row[3]) || 0;
            if (per_annum > 0 || units > 0) fallbackRows.push({ description: desc, units, rate, per_day: 0, per_month: 0, per_annum });
          }
          if (fallbackRows.length > 0) filtered.push({ key: 'misc', label: 'Imported Rows', rows: fallbackRows });
        }

        if (filtered.length === 0) {
          toast.error('No data found in Excel. Please check the file format.');
          setImportLoading(false);
          return;
        }

        setImportPreview({ sheetName: bestSheet, sheets: wb.SheetNames, sections: filtered });
        setShowImportModal(true);
      } catch (err) {
        toast.error('Failed to parse Excel: ' + err.message);
      } finally {
        setImportLoading(false);
        if (excelInputRef.current) excelInputRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const confirmImport = async () => {
    if (!importPreview || !reportId) return;
    const newRows = { ...dynamicRows };

    for (const section of importPreview.sections) {
      let subId = findSubIdForSection(section.key);
      if (!subId) {
        const lbl = section.label.toLowerCase();
        for (const main of tree) {
          for (const cat of main.categories) {
            for (const sub of cat.subCategories) {
              if ((sub.subName || '').toLowerCase().includes(lbl.slice(0, 4))) { subId = sub.subId; break; }
            }
            if (subId) break;
          }
          if (subId) break;
        }
      }
      if (!subId) continue;
      const key = String(subId);
      const importedRows = section.rows.map(r => ({ description: r.description, units: r.units, rate: r.rate, per_day: r.per_day, per_month: r.per_month, per_annum: r.per_annum }));
      newRows[key] = importMode === 'replace' ? importedRows : [...(newRows[key] || []), ...importedRows];
    }

    setDynamicRows(newRows);
    setShowImportModal(false);
    setImportPreview(null);
    toast.success(`✅ ${importPreview.sections.length} section(s) imported! Saving...`);
    setTimeout(() => saveReport(newRows), 300);
  };

  // ── Reset report data ─────────────────────────────────────────────────────────
  const resetReportData = () => {
    setConfirmModal({
      isOpen: true, title: 'Reset Report Data',
      message: 'This will clear all entered values. Are you sure?',
      onConfirm: () => {
        setDynamicRows({});
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  // ── Delete report ─────────────────────────────────────────────────────────────
  const deleteReport = () => {
    if (!reportId) return;
    setConfirmModal({
      isOpen: true, title: 'Move to archival?',
      message: 'Move this report to archival? It will no longer be visible here.',
      onConfirm: async () => {
        try {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${reportId}`, { withCredentials: true });
          setReportsList(prev => prev.filter(r => r.id !== reportId));
          resetState();
          toast.success('Report moved to archival');
        } catch { toast.error('Delete failed'); }
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  // ── Compute active project template ID and used category IDs ────────────────
  const { projectTemplateId, projectCategoryIds } = React.useMemo(() => {
    if (!reportId || !clientId) return { projectTemplateId: null, projectCategoryIds: null };
    const client = clientsList.find(c => c.id === Number(clientId));
    let clientTmplId = client ? client.template_id : null;
    if (client && !clientTmplId && client.template_ids) {
      let ids = typeof client.template_ids === 'string' ? JSON.parse(client.template_ids) : client.template_ids;
      if (ids && ids.length > 0) clientTmplId = ids[ids.length - 1];
    }
    const tmplId = templateId || clientTmplId;

    const catIds = new Set();
    if (templateSections && templateSections.length > 0) {
      templateSections.forEach(sec => {
        if (sec.main_section_id) catIds.add(Number(sec.main_section_id));
        if (sec.category_id) catIds.add(Number(sec.category_id));
        if (sec.sub_category_id) {
          const subId = Number(sec.sub_category_id);
          catIds.add(subId);
          // Add all level 4 items belonging to this sub_category_id from dbCategories
          dbCategories.forEach(c => {
            if (c.level === 4 && c.parent_id === subId) {
              catIds.add(c.id);
            }
          });
        }
        if (sec.items) {
          const raw = typeof sec.items === 'string' ? JSON.parse(sec.items) : sec.items;
          if (Array.isArray(raw)) raw.forEach(id => catIds.add(Number(id)));
        }
      });
    }

    // Add any dynamically created items present in dynamicRows
    Object.values(dynamicRows).forEach(rows => {
      if (Array.isArray(rows)) {
        rows.forEach(r => {
          if (r.description) {
            const found = dbCategories.find(c => c.name.toLowerCase() === r.description.toLowerCase());
            if (found) catIds.add(found.id);
          }
        });
      }
    });

    return { projectTemplateId: tmplId, projectCategoryIds: Array.from(catIds) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, clientId, clientsList, templateSections, dbCategories, dynamicRows]);

  const handleCloneConfirm = async (e) => {
    e.preventDefault();
    if (!reportId) return toast.error('No report selected to clone');
    if (!cloneModal.name.trim()) return toast.error('Enter a new name');
    if (cloneModal.cloneType === 'NEW_CLIENT' && !cloneModal.newClientId) return toast.error('Please select a new client');

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${reportId}/clone`,
        { name: cloneModal.name, cloneType: cloneModal.cloneType, newClientId: cloneModal.newClientId },
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Cloned successfully');
      setCloneModal({ isOpen: false, name: '', cloneType: 'SAME_CLIENT', newClientId: '' });
      // Fetch the updated reports list
      axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true })
        .then(listRes => setReportsList(listRes.data))
        .catch(() => { });
      // Auto-load the cloned project
      if (res.data?.id) {
        loadReport(res.data.id);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to clone report');
    } finally {
      setLoading(false);
    }
  };

  const handleClientCloneConfirm = async (e) => {
    e.preventDefault();
    if (!clientCloneModal.sourceReportId) return toast.error('Please select a project to clone');
    if (!clientCloneModal.newName.trim()) return toast.error('Enter a new name');

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${clientCloneModal.sourceReportId}/clone`,
        { name: clientCloneModal.newName, cloneType: 'NEW_CLIENT', newClientId: filterClientId },
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Cloned successfully');
      setClientCloneModal({ isOpen: false, sourceReportId: '', newName: '' });
      // Fetch the updated reports list
      axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true })
        .then(listRes => {
          setReportsList(listRes.data);
          // Auto-load the cloned project
          if (res.data?.id) loadReport(res.data.id);
        })
        .catch(() => { });
    } catch (err) {
      console.error(err);
      toast.error('Failed to clone report');
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <Layout title="Project Report" subtitle="Income & Expenditure Financial Projection Calculator">

      {/* ══ STICKY HEADER ════════════════════════════════════════════════════ */}
      <div className="sticky top-16 z-40 !bg-slate-950 pb-4 pt-1 space-y-4 border-b border-slate-800/50 shadow-2xl shadow-slate-950/80">
        <style>{`main > header { background-color: #020617 !important; backdrop-filter: none !important; }`}</style>

        {/* Report Selector */}
        <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-5 flex flex-wrap items-end gap-4 backdrop-blur-sm w-full">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Select Client</label>
            <Select
              options={[{ value: '', label: '-- All Clients --' }, ...clientsList.map(c => ({ value: c.id, label: c.client_name || `Client ${c.id}` }))]}
              value={{ value: filterClientId || '', label: filterClientId ? (clientsList.find(c => String(c.id) === String(filterClientId))?.client_name || `Client ${filterClientId}`) : '-- All Clients --' }}
              onChange={selected => {
                const newClientId = selected ? selected.value : '';
                setFilterClientId(newClientId);
                if (reportId && newClientId) {
                  const currentReport = reportsList.find(r => String(r.id) === String(reportId));
                  if (currentReport && String(currentReport.client_id) !== String(newClientId)) {
                    loadReport('');
                  }
                }
              }}
              styles={{
                ...selectStyles,
                control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: '44px', height: '44px', borderRadius: '0.75rem', backgroundColor: '#020617', borderColor: '#334155' }),
                input: (base) => ({ ...base, color: 'white', caretColor: 'auto' }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
              menuPortalTarget={document.body}
              placeholder="-- All Clients --"
              isClearable
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Select Report</label>
            <Select
              options={[{ value: '', label: '-- Select Existing Report --' }, ...(filterClientId ? reportsList.filter(r => String(r.client_id) === String(filterClientId)) : reportsList).map(r => ({ value: r.id, label: r.report_name }))]}
              value={{ value: reportId || '', label: reportId ? (reportsList.find(r => String(r.id) === String(reportId))?.report_name || '') : '-- Select Existing Report --' }}
              onChange={selected => loadReport(selected ? selected.value : '')}
              styles={{
                ...selectStyles,
                control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: '44px', height: '44px', borderRadius: '0.75rem', backgroundColor: '#020617', borderColor: '#334155' }),
                input: (base) => ({ ...base, color: 'white', caretColor: 'auto' }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
              menuPortalTarget={document.body}
              placeholder="-- Select Existing Report --"
              isClearable
            />
          </div>
          <button onClick={() => setShowNewReportModal(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition flex items-center gap-2 h-[42px]">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            New Report
          </button>

          {!reportId && filterClientId && reportsList.filter(r => String(r.client_id) === String(filterClientId)).length === 0 && (
            <button onClick={() => setClientCloneModal({ isOpen: true, sourceReportId: '', newName: '' })} className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition flex items-center gap-2 h-[42px]">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
              Clone Project
            </button>
          )}

          {reportId && (
            <>
              <button onClick={() => setCloneModal({ isOpen: true, name: reportName ? `${reportName} - Clone` : '', cloneType: 'SAME_CLIENT', newClientId: '' })} className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition flex items-center gap-2 h-[42px]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                Clone
              </button>
              <button onClick={deleteReport} title="Delete Report" className="bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 px-3 py-2.5 rounded-xl font-bold text-sm transition border border-rose-600/30 h-[42px] flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </>
          )}

          <button onClick={() => window.location.reload()} title="Reset & Refresh Page" className="bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 px-3 py-2.5 rounded-xl font-bold text-sm transition border border-slate-600/50 h-[42px] flex items-center justify-center ml-auto">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </div>

        {/* Settings Accordion */}
        {/* <div className="bg-slate-900/60 border border-slate-700 rounded-2xl mb-4 overflow-hidden backdrop-blur-sm">
          <button onClick={() => toggle('settings')} className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/60 transition group">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center text-slate-400 group-hover:text-emerald-400 group-hover:bg-emerald-400/10 transition">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <span className="font-bold text-gray-300 text-sm uppercase tracking-widest group-hover:text-white transition">Configuration Settings</span>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${openSections.settings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {openSections.settings && (
            <div className="p-5 flex flex-wrap gap-6 items-end border-t border-slate-700/50 bg-slate-900/30">
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Working Days/Month</label>
                <input type="number" min="1" max="31" value={workingDays} onChange={e => setWorkingDays(parseInt(e.target.value) || 26)}
                  className="w-32 bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-emerald-500 font-mono text-center" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Hours/Day</label>
                <input type="number" min="1" max="24" value={workingHours} onChange={e => setWorkingHours(parseInt(e.target.value) || 8)}
                  className="w-24 bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-emerald-500 font-mono text-center" />
              </div>
            </div>
          )}
        </div> */}

        {/* ══ GORGEOUS PROJECT 5-TAB NAVIGATION BAR ════════════════════════════ */}
        {reportId && (
          <div className="bg-slate-900/80 border border-slate-700 rounded-2xl p-2 mb-2 flex flex-wrap gap-2 shadow-2xl backdrop-blur-md">
            <button
              onClick={() => setActiveTab('projections')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'projections'
                ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              📊 Projections
            </button>

            {/* 🏢 Cost of The Project */}
            <button
              onClick={() => setActiveTab('cost_of_project')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'cost_of_project'
                ? 'bg-gradient-to-r from-violet-600/30 to-purple-600/30 border-violet-500/50 text-violet-300 shadow-[0_0_15px_rgba(139,92,246,0.25)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              &#127970; Cost of The Project
            </button>

            {/* 🍰 Project Percentage */}
            <button
              onClick={() => setActiveTab('project_percentage')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'project_percentage'
                ? 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 border-pink-500/50 text-pink-300 shadow-[0_0_15px_rgba(236,72,153,0.2)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
              &#127830; Project Percentage
            </button>

            <button
              onClick={() => setActiveTab('working_capital')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'working_capital'
                ? 'bg-gradient-to-r from-indigo-500/20 to-violet-500/20 border-indigo-500/50 text-indigo-300 shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              💼 Working Capital
            </button>

            <button
              onClick={() => setActiveTab('masters')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'masters'
                ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/50 text-amber-300 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              📚 Masters
            </button>

            <button
              onClick={() => setActiveTab('fy_projection')}
              className={`flex-1 min-w-[140px] py-3.5 px-4 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 border ${activeTab === 'fy_projection'
                ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border-teal-500/50 text-teal-300 shadow-[0_0_15px_rgba(20,184,166,0.2)]'
                : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
            >
              <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              📈 FY Projection
            </button>

          </div>
        )}

        {/* ══ FROZEN PROJECTIONS CONTENT ══════════════════════════════════════ */}
        {activeTab === 'projections' && reportId && (
          <div className="space-y-4 pb-2 pt-2">
            {/* Active Report Banner */}
            <div className="flex flex-wrap items-center gap-4 bg-slate-900/40 border border-emerald-500/20 rounded-2xl px-5 py-3">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest font-mono">Active Report</p>
                <p className="font-black text-white text-lg">{reportName}</p>
              </div>
              <p className="ml-auto text-xs text-gray-500 border-l border-slate-700 pl-4 pr-4">
                Working: {workingDays} days/month × {workingHours} hrs/day
              </p>
              <div className="flex items-center gap-3">
                {/* Hidden file input for Excel import */}
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={e => { if (e.target.files[0]) parseExcelFile(e.target.files[0]); }}
                />
                <button
                  onClick={() => {
                    if (!reportId) { toast.error('Select a report first'); return; }
                    if (!templateSections || templateSections.length === 0) {
                      toast.error('⚠️ Please assign a template to this client in Clients → Template before importing Excel data.');
                      return;
                    }
                    excelInputRef.current?.click();
                  }}
                  disabled={importLoading}
                  className="bg-violet-700/20 hover:bg-violet-700/40 text-violet-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-violet-700/30 flex items-center gap-2 disabled:opacity-40"
                >
                  {importLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  )}
                  Import Excel
                </button>
                <button
                  onClick={exportToExcel}
                  className="bg-green-700/20 hover:bg-green-700/40 text-green-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-green-700/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export XLS
                </button>
                <button
                  onClick={exportToPDF}
                  className="bg-rose-700/20 hover:bg-rose-700/40 text-rose-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-rose-700/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export PDF
                </button>
                <button onClick={resetReportData} className="bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 px-3 py-2 rounded-xl font-bold text-sm transition border border-amber-600/30 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3m-3-3v12" /></svg>
                  Reset
                </button>
                <button onClick={saveReport} disabled={saving}
                  className="bg-gradient-to-r from-emerald-500 to-teal-400 text-black px-5 py-2 rounded-xl font-bold text-sm transition hover:shadow-lg hover:shadow-emerald-500/20 disabled:opacity-40 flex items-center gap-2">
                  {saving ? (<><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving...</>) : (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>Save</>)}
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-5">
                <p className="text-xs text-rose-400 font-mono uppercase tracking-wider">Total Expenditure (Annual)</p>
                <p className="text-3xl font-extrabold text-white mt-2">₹ {fmt(totalExpAnnum)}</p>
                <p className="text-xs text-gray-500 mt-1">Per Month: ₹ {fmt(totalExpAnnum / 12)}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5">
                <p className="text-xs text-emerald-400 font-mono uppercase tracking-wider">Total Revenue (Annual)</p>
                <p className="text-3xl font-extrabold text-white mt-2">₹ {fmt(totalIncAnnum)}</p>
                <p className="text-xs text-gray-500 mt-1">Per Month: ₹ {fmt(totalIncAnnum / 12)}</p>
              </div>
              <div className={`${netProfitLoss >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20'} border rounded-2xl p-5`}>
                <p className={`text-xs font-mono uppercase tracking-wider ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>Net {netProfitLoss >= 0 ? 'Profit' : 'Loss'} (Annual)</p>
                <p className={`text-3xl font-extrabold mt-2 ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>₹ {fmt(Math.abs(netProfitLoss))}</p>
                <p className="text-xs text-gray-500 mt-1">Per Month: ₹ {fmt(Math.abs(netProfitLoss) / 12)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══ TAB CONTENT ══════════════════════════════════════════════════════ */}
      {activeTab === 'fy_projection' && (
        <div className="bg-slate-950 border border-teal-500/20 rounded-3xl p-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-800">
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h2 className="font-black text-white text-lg">Bank Financial Projection</h2>
              <p className="text-xs text-teal-400/70">{projectionYears}-Year Financial Forecast — Auto-calculated from single growth %</p>
            </div>
          </div>
          <BankFinancialProjection
            isEmbedded={true}
            reportId={reportId}
            reportName={reportName}
            tree={tree}
            sectionTotal={sectionTotal}
            onConfigLoad={(cfg) => setProjectionYears(cfg.numYears)}
          />
        </div>
      )}


      {activeTab === 'working_capital' && reportId && (
        <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6 shadow-2xl">
          <WorkingCapitalMaster isEmbedded={true} selectedReportId={reportId} />
        </div>
      )}

      {activeTab === 'masters' && (
        <div className="space-y-6">
          {/* Masters Tab Header */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl px-6 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
            </div>
            <div>
              <h2 className="font-black text-white text-lg">Project Master Configuration</h2>
              <p className="text-xs text-amber-400/70">Showing Categories and Templates assigned to and used by this active project report.</p>
            </div>
          </div>

          {/* Category Master Section */}
          <div className="bg-slate-950 border border-amber-500/20 rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z" /></svg>
              </div>
              <h3 className="font-bold text-amber-300 text-base uppercase tracking-wider">Project Categories</h3>
            </div>
            <div className="p-4">
              <CategoryMaster isEmbedded={true} projectCategoryIds={projectCategoryIds} />
            </div>
          </div>

          {/* Templates Section */}
          <div className="bg-slate-950 border border-orange-500/20 rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-orange-500/10 border-b border-orange-500/20 px-6 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
              </div>
              <h3 className="font-bold text-orange-300 text-base uppercase tracking-wider">Project Template</h3>
            </div>
            <div className="p-4">
              <TemplatesList isEmbedded={true} projectTemplateId={projectTemplateId} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'cost_of_project' && reportId && (
        <div className="bg-slate-950 border border-violet-500/20 rounded-3xl p-5 shadow-2xl">
          <CostOfProject isEmbedded={true} selectedReportId={reportId} />
        </div>
      )}

      {activeTab === 'project_percentage' && reportId && (
        <div className="bg-slate-950 border border-pink-500/20 rounded-3xl p-6 shadow-2xl mb-4">
          <ProjectPercentageMaster isEmbedded={true} selectedReportId={reportId} />
        </div>
      )}

      {activeTab === 'projections' && (
        <div className="space-y-6">
          {!reportId ? (
            <div className="flex flex-col items-center justify-center py-20 bg-slate-900/30 border border-slate-800/50 rounded-3xl mt-2">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 border border-slate-700 shadow-inner">
                <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Report Selected</h3>
              <p className="text-slate-400 text-sm">Please select a client and a report from the dropdown above to view projections.</p>
            </div>
          ) : (
            <>


              {/* ══ FY PERCENTAGE ALLOCATION BANNER ══════════════════════════════════ */}
              {reportId && fyPercentages.length > 0 && (() => {
                const isFyOpen = openSections['fyBanner'];
                return (
                  <div className="bg-slate-900/60 border border-violet-500/30 rounded-2xl p-6 shadow-xl shadow-violet-500/10">
                    <div className={`flex flex-wrap items-center justify-between gap-4 cursor-pointer select-none ${isFyOpen ? 'mb-4 pb-4 border-b border-slate-800' : ''}`} onClick={() => toggle('fyBanner')}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400 font-bold">
                          %
                        </div>
                        <div>
                          <h3 className="text-lg font-black text-white">Project Allocation / Realization Share</h3>
                          <p className="text-xs text-gray-400">Values calculated based on Financial Year percentages configured in Master</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <a
                          href="/project-percentage"
                          onClick={(e) => e.stopPropagation()}
                          className="bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-500/30 px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2"
                        >
                          Manage Percentages
                        </a>
                        <svg className={`w-6 h-6 text-violet-400 transition-transform ${isFyOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                    {isFyOpen && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {fyPercentages.map(fy => {
                          const pctVal = parseFloat(fy.percentage_value) || 0;
                          const incShare = totalIncAnnum * (pctVal / 100);
                          const expShare = totalExpAnnum * (pctVal / 100);
                          const netShare = netProfitLoss * (pctVal / 100);
                          return (
                            <div key={fy.id} className="bg-slate-950/60 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition">
                              <div className="flex items-center justify-between mb-2">
                                <span className="bg-violet-500/20 text-violet-300 font-bold text-xs px-2.5 py-1 rounded-full border border-violet-500/30">
                                  FY {fy.financial_year}
                                </span>
                                <span className="text-sm font-black font-mono text-cyan-400">{pctVal.toFixed(2)}%</span>
                              </div>
                              <p className="text-xs font-semibold text-gray-300 mb-3 truncate">{fy.percentage_label} {fy.notes ? `(${fy.notes})` : ''}</p>
                              <div className="space-y-1.5 pt-2 border-t border-slate-800/80 text-xs font-mono">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Revenue Share:</span>
                                  <span className="font-bold text-emerald-400">₹ {fmt(incShare)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Expense Share:</span>
                                  <span className="font-bold text-rose-400">₹ {fmt(expShare)}</span>
                                </div>
                                <div className="flex justify-between pt-1 border-t border-slate-800">
                                  <span className="text-gray-400 font-bold">Net Profit Share:</span>
                                  <span className="font-bold text-cyan-400">₹ {fmt(netShare)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ══ NO TEMPLATE WARNING ══════════════════════════════════════════════ */}
              {reportId && (!templateSections || templateSections.length === 0) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 text-center mb-4">
                  <p className="text-amber-300 font-bold text-lg mb-1">⚠️ No Template Sections Found</p>
                  <p className="text-amber-400/70 text-sm">This report's client has no template assigned, or the template has no sections defined.</p>
                  <p className="text-amber-400/50 text-xs mt-2">Go to <strong>Clients → Template</strong> to assign and configure a template before importing Excel data.</p>
                </div>
              )}

              {/* ══ DYNAMIC SECTIONS ═════════════════════════════════════════════════ */}
              <div className="flex gap-4 h-fit min-h-[300px] max-h-[650px] items-stretch w-full">
                {/* PANE 1: Main Sections */}
                <div className="w-[300px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-auto min-h-0">
                  <div className="p-4 bg-slate-800/80 border-b border-slate-700">
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 1 (Main Section)</h3>
                    <p className="text-xs text-slate-400 mt-1">{tree.length} items</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                    {tree.map((main, idx) => {
                      const mainTotal = main.categories.reduce((acc, cat) =>
                        acc + cat.subCategories.reduce((a, sub) => a + sectionTotal(sub.subId, sub.subName), 0), 0);
                      const isSelected = selectedMainId === main.mainId;
                      const textClass = 'text-green-300';
                      return (
                        <div key={main.mainId} onClick={() => { setSelectedMainId(main.mainId); setSelectedSubId(null); }} className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-green-500/10 border-green-500 shadow-green-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}>
                          <div className="flex items-center gap-2 overflow-hidden">
                            <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-lg border bg-green-500/20 text-green-300 border-green-500/40`}>{idx + 1}</span>
                            <h4 className={`font-bold text-sm truncate ${isSelected ? textClass : 'text-slate-200'}`}>{main.mainName}</h4>
                          </div>
                          <div className={`text-xs font-mono font-bold text-right ${isSelected ? textClass : 'text-slate-400'}`}>
                            &#8377; {fmt(mainTotal)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* PANE 2: Sub Heads */}
                {(() => {
                  const activeMain = tree.find(m => m.mainId === selectedMainId);
                  if (!activeMain) return null;

                  const allSubHeads = [];
                  activeMain.categories.forEach(cat => {
                    cat.subCategories.forEach(sub => {
                      allSubHeads.push({ ...sub, catName: cat.catName, catId: cat.catId });
                    });
                  });

                  return (
                    <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-auto min-h-0">
                      <div className="p-4 bg-slate-800/80 border-b border-slate-700">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 2 (Sub-Heads)</h3>
                        <p className="text-xs text-slate-400 mt-1">{allSubHeads.length} items</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                        {allSubHeads.length === 0 ? (
                          <div className="text-center py-10 text-slate-500 text-xs italic">No sub-heads yet</div>
                        ) : allSubHeads.map((sub, idx) => {
                          const isSelected = selectedSubId === sub.subId;
                          const rows = computedRows(sub.subId);
                          const grossTotal = rows.reduce((s, r) => s + r.per_annum, 0);
                          const perc = getProfitPerc(sub.subId);
                          const subTotal = (grossTotal * perc) / 100;

                          return (
                            <div key={sub.subId} onClick={() => setSelectedSubId(sub.subId)} className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-fuchsia-500/10 border-fuchsia-500 shadow-fuchsia-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}>
                              <div className="flex items-center gap-2 justify-between">
                                <div className="flex flex-col gap-1 overflow-hidden w-full">
                                  <p className="text-[10px] uppercase text-slate-500 truncate">{sub.catName}</p>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-[10px] font-black px-2 py-0.5 rounded border bg-slate-700 text-slate-300 border-slate-600`}>{idx + 1}</span>
                                    <h4 className={`font-bold text-sm truncate ${isSelected ? 'text-fuchsia-300' : 'text-slate-200'}`}>{sub.subName}</h4>
                                  </div>
                                </div>
                              </div>
                              <div className={`text-xs font-mono font-bold text-right ${isSelected ? 'text-fuchsia-300' : 'text-slate-400'}`}>
                                &#8377; {fmt(subTotal)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* PANE 3: Table Data */}
                {(() => {
                  const activeMain = tree.find(m => m.mainId === selectedMainId);
                  if (!activeMain) return null;

                  let activeSub = null;
                  let activeCat = null;
                  activeMain.categories.forEach(cat => {
                    cat.subCategories.forEach(sub => {
                      if (sub.subId === selectedSubId) {
                        activeSub = sub;
                        activeCat = cat;
                      }
                    });
                  });

                  if (!activeSub) {
                    return (
                      <div className="flex-1 flex flex-col items-center justify-center bg-slate-900/50 border border-slate-700/50 rounded-2xl h-fit max-h-full min-h-[300px]">
                        <svg className="w-16 h-16 text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <p className="text-slate-400 text-sm">Select a sub-head to view and edit its rows</p>
                      </div>
                    );
                  }

                  const rows = computedRows(activeSub.subId);
                  const rawRows = dynamicRows[String(activeSub.subId)] || [];
                  const grossTotal = rows.reduce((s, r) => s + r.per_annum, 0);
                  const grossDay = rows.reduce((s, r) => s + r.per_day, 0);
                  const grossMonth = rows.reduce((s, r) => s + r.per_month, 0);
                  const perc = getProfitPerc(activeSub.subId);
                  const subTotal = (grossTotal * perc) / 100;
                  const profitDay = (grossDay * perc) / 100;
                  const profitMonth = (grossMonth * perc) / 100;
                  const isProfitCenter = perc !== 100;

                  const selectedItems = rawRows.map(r => r.description).filter(Boolean);
                  const itemOptions = dbCategories
                    .filter(cat => cat.level === 4 && cat.parent_id === Number(activeSub.subId))
                    .map(cat => ({ label: cat.name, value: cat.name }))
                    .filter(opt => !selectedItems.includes(opt.value));

                  return (
                    <div className="flex-1 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner h-auto min-h-0">
                      <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 3 (Table Data)</h3>
                          <p className="text-xs text-slate-400 mt-1">{activeCat.catName} &gt; {activeSub.subName}</p>
                        </div>
                        <div className="text-sm font-mono font-bold px-3 py-1.5 rounded-lg border text-fuchsia-400/90 bg-fuchsia-500/10 border-fuchsia-500/20">
                          Sub-Total: &#8377; {fmt(subTotal)}
                        </div>
                      </div>
                      <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar p-4">
                        <div className="overflow-x-auto rounded-xl border border-slate-800">
                          <table className="w-full text-sm">
                            <TableHeader cols={['Description', 'Units', 'Rate (₹)', 'Per Day', 'Per Month', 'Per Annum']} />
                            <tbody>
                              {rows.map((row, i) => (
                                <tr key={i} className="border-b border-slate-800 hover:bg-slate-900/40 bg-slate-900/20">
                                  <TextCell options={itemOptions} value={rawRows[i]?.description || ''} onChange={v => updateDynRow(activeSub.subId, i, 'description', v)} />
                                  <InputCell value={rawRows[i]?.units || 0} onChange={v => updateDynRow(activeSub.subId, i, 'units', v)} />
                                  <InputCell value={rawRows[i]?.rate || 0} onChange={v => updateDynRow(activeSub.subId, i, 'rate', v)} />
                                  <CalcCell value={row.per_day} />
                                  <CalcCell value={row.per_month} />
                                  <CalcCell value={row.per_annum} highlight />
                                  <RemoveBtn onClick={() => removeDynRow(activeSub.subId, i)} />
                                </tr>
                              ))}
                              {isProfitCenter && (
                                <TotalRow label={`Gross Sales (${activeSub.subName})`} cols={6} values={[grossDay, grossMonth, grossTotal]} />
                              )}
                              <TotalRow label={isProfitCenter ? `Profit @ ${perc}%` : `Total ${activeSub.subName}`} cols={6} values={[profitDay, profitMonth, subTotal]} />
                            </tbody>
                          </table>
                        </div>
                        <AddRowBtn onClick={() => addDynRow(activeSub.subId)} label={`Add ${activeSub.subName} Row`} />
                      </div>
                    </div>
                  );
                })()}
              </div>
              <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>

              {/* ══ NET PROFIT / LOSS ════════════════════════════════════════════════ */}
              {reportId && (
                <div className={`rounded-3xl p-6 border-2 ${netProfitLoss >= 0 ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`}>
                  <h3 className={`text-sm font-bold mb-4 uppercase tracking-widest ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>📊 Financial Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-900/60 rounded-2xl p-4 border border-rose-500/20">
                      <p className="text-xs text-rose-400 uppercase tracking-wider mb-2">Total Expenditure</p>
                      <p className="text-2xl font-extrabold text-white">₹ {fmt(totalExpAnnum)}</p>
                      <div className="mt-3 space-y-1 text-xs text-gray-500">
                        {tree.map(m =>
                          m.categories.map(c => {
                            const isExp = m.mainName.toUpperCase() === 'INCOME AND EXPENDITURE' ? c.isCatExpenditure : m.isExpenditure;
                            if (!isExp) return null;
                            return c.subCategories.map(s => (
                              <div key={s.subId} className="flex justify-between">
                                <span>{s.subName}</span>
                                <span className="font-mono text-gray-400">₹ {fmt(sectionTotal(s.subId))}</span>
                              </div>
                            ));
                          })
                        )}
                      </div>
                    </div>
                    <div className="bg-slate-900/60 rounded-2xl p-4 border border-emerald-500/20">
                      <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">Total Revenue</p>
                      <p className="text-2xl font-extrabold text-white">₹ {fmt(totalIncAnnum)}</p>
                      <div className="mt-3 space-y-1 text-xs text-gray-500">
                        {tree.map(m =>
                          m.categories.map(c => {
                            const isInc = m.mainName.toUpperCase() === 'INCOME AND EXPENDITURE' ? !c.isCatExpenditure : !m.isExpenditure;
                            if (!isInc) return null;
                            return c.subCategories.map(s => (
                              <div key={s.subId} className="flex justify-between">
                                <span>{s.subName}</span>
                                <span className="font-mono text-gray-400">₹ {fmt(sectionTotal(s.subId))}</span>
                              </div>
                            ));
                          })
                        )}
                      </div>
                    </div>
                    <div className={`rounded-2xl p-4 border-2 ${netProfitLoss >= 0 ? 'bg-emerald-500/20 border-emerald-500/40' : 'bg-rose-500/20 border-rose-500/40'}`}>
                      <p className={`text-xs uppercase tracking-wider mb-2 ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>Net {netProfitLoss >= 0 ? 'Profit' : 'Loss'}</p>
                      <p className={`text-3xl font-extrabold ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>₹ {fmt(Math.abs(netProfitLoss))}</p>
                      <p className="text-xs text-gray-500 mt-2">Per Month: ₹ {fmt(Math.abs(netProfitLoss) / 12)}</p>
                      <p className="text-xs text-gray-500">Per Day: ₹ {fmt(Math.abs(netProfitLoss) / (workingDays * 12))}</p>
                      <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${netProfitLoss >= 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-400' : 'bg-gradient-to-r from-rose-500 to-pink-400'}`}
                          style={{ width: `${Math.min(100, totalIncAnnum > 0 ? (Math.abs(netProfitLoss) / totalIncAnnum) * 100 : 0)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}


      {/* ══ CLIENT CLONE MODAL ═════════════════════════════════════════════════ */}
      {clientCloneModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="text-amber-500">📑</span> Clone Project to Client
              </h3>
              <button onClick={() => setClientCloneModal({ isOpen: false, sourceReportId: '', newName: '' })} className="text-slate-500 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleClientCloneConfirm} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Select Source Project</label>
                <select
                  autoFocus
                  className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-amber-500 transition"
                  value={clientCloneModal.sourceReportId}
                  onChange={e => setClientCloneModal({ ...clientCloneModal, sourceReportId: e.target.value })}
                  required
                >
                  <option value="">-- Select Project to Clone --</option>
                  {reportsList.map(r => <option key={r.id} value={r.id}>{r.report_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">New Project Name</label>
                <input type="text" required className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-amber-500 transition"
                  value={clientCloneModal.newName} onChange={e => setClientCloneModal({ ...clientCloneModal, newName: e.target.value })}
                  placeholder="e.g., Apollo Pharmacy Report - Clone" />
              </div>
              <div className="pt-2">
                <button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold py-3 rounded-xl transition flex justify-center items-center gap-2">
                  {loading ? 'Cloning...' : 'Assign Cloned Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ EXCEL IMPORT MODAL ═════════════════════════════════════════════════ */}
      {showNewReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-6">Create New Project Report</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wide">Select Client</label>
                <Select
                  options={[{ value: '', label: '-- No Client --' }, ...clientsList.map(c => ({ value: c.id, label: c.client_name }))]}
                  value={{ value: clientId || '', label: clientId ? (clientsList.find(c => String(c.id) === String(clientId))?.client_name || '') : '-- No Client --' }}
                  onChange={selected => setClientId(selected ? selected.value : '')}
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: '48px', height: '48px', borderRadius: '0.75rem', backgroundColor: '#020617', borderColor: '#334155' }),
                    input: (base) => ({ ...base, color: 'white', caretColor: 'auto' }),
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                  menuPortalTarget={document.body}
                  placeholder="-- No Client --"
                />
                {clientsList.length === 0 && (
                  <p className="text-xs text-amber-400 mt-1.5">⚠ No clients with templates assigned. Go to Clients → Template first.</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wide">Report Name *</label>
                <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} placeholder="e.g., FY 2025-26 Budget Projection"
                  className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wide">Report Date</label>
                <Flatpickr options={{ altInput: true, altFormat: 'd-m-Y', dateFormat: 'Y-m-d' }} placeholder="dd-mm-yyyy"
                  value={reportDate} onChange={(_, dateStr) => setReportDate(dateStr)}
                  className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white outline-none focus:border-emerald-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 font-semibold mb-1">Working Days/Month</label>
                  <input type="number" value={workingDays} onChange={e => setWorkingDays(parseInt(e.target.value) || 26)} min="1" max="31"
                    className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white font-mono text-center outline-none focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 font-semibold mb-1">Working Hours/Day</label>
                  <input type="number" value={workingHours} onChange={e => setWorkingHours(parseInt(e.target.value) || 8)} min="1" max="24"
                    className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white font-mono text-center outline-none focus:border-emerald-500" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowNewReportModal(false)} className="flex-1 bg-slate-800 text-white py-3 rounded-xl font-bold hover:bg-slate-700 transition">Cancel</button>
              <button onClick={createReport} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black py-3 rounded-xl font-bold transition">Create Report</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ LOADING OVERLAY ══════════════════════════════════════════════════ */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-emerald-500/20 border-t-emerald-400 animate-spin" />
            <p className="text-sm text-gray-400 font-mono">Loading report data...</p>
          </div>
        </div>
      )}

      {/* ── CLONE MODAL ────────────────────────────────────────────────────────────── */}
      {cloneModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleCloneConfirm} className="bg-slate-900 border border-cyan-500/30 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-cyan-500/10 p-5 flex items-center gap-4 border-b border-cyan-500/20">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
              </div>
              <h3 className="text-lg font-bold text-cyan-400">Clone Project</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-xs text-gray-400 font-semibold mb-2 uppercase tracking-widest">Clone Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="cloneType"
                      value="SAME_CLIENT"
                      checked={cloneModal.cloneType === 'SAME_CLIENT'}
                      onChange={() => setCloneModal({ ...cloneModal, cloneType: 'SAME_CLIENT', newClientId: '' })}
                      className="accent-cyan-500"
                    />
                    Same Client
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="cloneType"
                      value="NEW_CLIENT"
                      checked={cloneModal.cloneType === 'NEW_CLIENT'}
                      onChange={() => setCloneModal({ ...cloneModal, cloneType: 'NEW_CLIENT' })}
                      className="accent-cyan-500"
                    />
                    New Client
                  </label>
                </div>
              </div>

              {cloneModal.cloneType === 'NEW_CLIENT' && (
                <div className="mb-4">
                  <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Select New Client</label>
                  <Select
                    options={[{ value: '', label: '-- Select Client --' }, ...clientsList.map(c => ({ value: c.id, label: c.client_name || `Client ${c.id}` }))]}
                    value={{ value: cloneModal.newClientId || '', label: cloneModal.newClientId ? (clientsList.find(c => String(c.id) === String(cloneModal.newClientId))?.client_name || `Client ${cloneModal.newClientId}`) : '-- Select Client --' }}
                    onChange={selected => setCloneModal({ ...cloneModal, newClientId: selected ? selected.value : '' })}
                    styles={{
                      ...selectStyles,
                      control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: '48px', height: '48px', borderRadius: '0.75rem', backgroundColor: '#020617', borderColor: '#334155' }),
                      input: (base) => ({ ...base, color: 'white', caretColor: 'auto' }),
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                    menuPortalTarget={document.body}
                    placeholder="-- Select Client --"
                  />
                </div>
              )}

              <p className="text-gray-300 text-sm mb-4">Enter a name for the cloned project:</p>
              <input
                type="text"
                value={cloneModal.name}
                onChange={e => setCloneModal({ ...cloneModal, name: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white outline-none focus:border-cyan-500 transition"
                autoFocus
              />
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setCloneModal({ isOpen: false, name: '', cloneType: 'SAME_CLIENT', newClientId: '' })} className="flex-1 bg-slate-800 text-gray-300 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-700 transition">Cancel</button>
                <button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-2.5 rounded-xl text-sm font-semibold transition">Clone Project</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ══ CONFIRM MODAL ════════════════════════════════════════════════════ */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <h3 className="text-lg font-bold text-rose-400">{confirmModal.title}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm leading-relaxed">{confirmModal.message}</p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
                  className="flex-1 bg-slate-800 text-gray-300 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-700 transition">Cancel</button>
                <button onClick={confirmModal.onConfirm}
                  className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-semibold transition">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ EXCEL IMPORT PREVIEW MODAL ════════════════════════════════════════ */}
      {showImportModal && importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-violet-500/30 rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600/20 to-indigo-600/20 p-6 flex items-center justify-between border-b border-violet-500/20">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400 shadow-inner">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <h3 className="text-xl font-black text-white">Excel Import Preview</h3>
                  <p className="text-xs text-violet-300/70 mt-0.5">Sheet Detected: <span className="font-mono text-cyan-400 font-bold">{importPreview.sheetName}</span></p>
                </div>
              </div>
              <button onClick={() => { setShowImportModal(false); setImportPreview(null); }} className="text-gray-400 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Content / Section List */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Import Mode Selection */}
              <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-200">Import Mode</h4>
                  <p className="text-xs text-gray-500">Choose how imported rows should merge with existing data</p>
                </div>
                <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-700">
                  <button
                    type="button"
                    onClick={() => setImportMode('replace')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition ${importMode === 'replace' ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-gray-400 hover:text-white'}`}
                  >
                    Replace Existing Rows
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMode('append')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition ${importMode === 'append' ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-gray-400 hover:text-white'}`}
                  >
                    Append to Existing Rows
                  </button>
                </div>
              </div>

              {/* Sections & Rows */}
              <div className="space-y-6">
                {importPreview.sections.map((sec, sIdx) => (
                  <div key={sIdx} className="bg-slate-950/40 border border-slate-800 rounded-2xl overflow-hidden">
                    <div className="bg-slate-800/40 px-5 py-3 border-b border-slate-700/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="px-2.5 py-1 rounded-lg bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs font-bold uppercase tracking-wider">
                          {sec.key}
                        </span>
                        <span className="text-sm font-bold text-white">{sec.label}</span>
                      </div>
                      <span className="text-xs font-mono text-gray-400">{sec.rows.length} row{sec.rows.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-slate-900/40">
                            <th className="py-2.5 px-4">Description</th>
                            <th className="py-2.5 px-3 text-right">Units</th>
                            <th className="py-2.5 px-3 text-right">Rate</th>
                            <th className="py-2.5 px-3 text-right">Per Day</th>
                            <th className="py-2.5 px-3 text-right">Per Month</th>
                            <th className="py-2.5 px-4 text-right">Per Annum</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50 text-xs">
                          {sec.rows.map((r, rIdx) => (
                            <tr key={rIdx} className="hover:bg-slate-800/20 transition">
                              <td className="py-2.5 px-4 font-medium text-gray-200">{r.description}</td>
                              <td className="py-2.5 px-3 text-right font-mono text-gray-400">{fmt(r.units)}</td>
                              <td className="py-2.5 px-3 text-right font-mono text-gray-400">{fmt(r.rate)}</td>
                              <td className="py-2.5 px-3 text-right font-mono text-gray-400">{fmt(r.per_day)}</td>
                              <td className="py-2.5 px-3 text-right font-mono text-gray-400">{fmt(r.per_month)}</td>
                              <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-400">₹ {fmt(r.per_annum)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-950 p-6 border-t border-slate-800 flex items-center justify-between gap-4">
              <p className="text-xs text-gray-400 font-medium">
                Found <span className="text-white font-bold">{importPreview.sections.length}</span> sections to import.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowImportModal(false); setImportPreview(null); }}
                  className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-gray-300 font-bold text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmImport}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold text-sm shadow-xl shadow-violet-600/30 transition flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                  Confirm & Import Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default HospitalReport;
